import * as Dialog from '@radix-ui/react-dialog';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/Button';
import { ErrorNotice } from '../../components/ErrorNotice';
import type { Customer, CustomerStatus } from './api';
import { useCreateCustomer, useUpdateCustomer } from './queries';
import { Select } from '../../components/Select';
import { CrmSuggestInput } from './CrmSuggestInput';

interface CustomerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Beim Aendern gesetzt; ohne ihn legt der Dialog an. */
  customer?: Customer;
}

const STATUSES: readonly CustomerStatus[] = ['prospect', 'active', 'inactive'];

/**
 * Anlegen und Aendern eines Kunden -- F2-01.
 *
 * Ein Dialog fuer beides. Zwei Masken mit denselben acht Feldern waeren zwei
 * Stellen, an denen ein neues Feld vergessen werden kann.
 *
 * Die Kundennummer bleibt leer, wenn sie der Dienst vergeben soll. Das ist
 * der Regelfall -- wer Nummern erfindet, erzeugt Doubletten.
 */
export function CustomerDialog({ open, onOpenChange, customer }: CustomerDialogProps) {
  const { t } = useTranslation();
  const create = useCreateCustomer();
  const update = useUpdateCustomer();

  const [values, setValues] = useState({
    name: customer?.name ?? '',
    customer_number: customer?.customer_number ?? '',
    status: customer?.status ?? 'active',
    industry: customer?.industry ?? '',
    vat_id: customer?.vat_id ?? '',
    website: customer?.website ?? '',
    notes: customer?.notes ?? '',
    is_vip: customer?.is_vip ?? false,
    // Vorgabe an: Ein neuer Kunde hat dem Versand nicht widersprochen.
    accepts_email_notifications: customer?.accepts_email_notifications ?? true,
    // Eine Zeile je Domaene. Ein Feld mit Kommas waere kuerzer und lesbarer
    // nur fuer den, der es schreibt -- drei Domaenen untereinander sind mit
    // einem Blick zu pruefen, drei durch Kommas getrennte nicht.
    mail_domains: (customer?.mail_domains ?? []).join('\n'),
  });

  const mutation = customer ? update : create;

  const set = <K extends keyof typeof values>(field: K, value: (typeof values)[K]) => {
    setValues((current) => ({ ...current, [field]: value }));
  };

  /** Leere Zeilen fallen weg; der Rest geht so hinaus, wie er eingegeben wurde. */
  const domainList = () =>
    values.mail_domains
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);

  const submit = () => {
    const payload = {
      name: values.name.trim(),
      mail_domains: domainList(),
      status: values.status,
      is_vip: values.is_vip,
      accepts_email_notifications: values.accepts_email_notifications,
      ...(values.customer_number ? { customer_number: values.customer_number.trim() } : {}),
      ...(values.industry ? { industry: values.industry } : {}),
      ...(values.vat_id ? { vat_id: values.vat_id } : {}),
      ...(values.website ? { website: values.website } : {}),
      ...(values.notes ? { notes: values.notes } : {}),
    };

    if (customer) {
      update.mutate(
        { customerId: customer.id, payload },
        {
          onSuccess: () => {
            onOpenChange(false);
          },
        },
      );
    } else {
      create.mutate(payload, {
        onSuccess: () => {
          onOpenChange(false);
        },
      });
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="dialog__overlay" />
        <Dialog.Content className="card dialog__content">
          <Dialog.Title asChild>
            <h2 style={{ marginBottom: 'var(--space-4)' }}>
              {customer ? t('crm.editCustomer') : t('crm.newCustomer')}
            </h2>
          </Dialog.Title>
          <Dialog.Description className="visually-hidden">{t('crm.subtitle')}</Dialog.Description>

          {mutation.isError ? <ErrorNotice error={mutation.error} /> : null}

          <form
            onSubmit={(event) => {
              event.preventDefault();
              submit();
            }}
            noValidate
          >
            <div className="field">
              <label className="field__label" htmlFor="customer-name">
                {t('masterdata.name')}
              </label>
              <input
                id="customer-name"
                className="field__control"
                value={values.name}
                autoFocus
                onChange={(event) => {
                  set('name', event.target.value);
                }}
              />
            </div>

            <div className="field">
              <label className="field__label" htmlFor="customer-number">
                {t('crm.customerNumber')}
              </label>
              <input
                id="customer-number"
                className="field__control"
                value={values.customer_number}
                onChange={(event) => {
                  set('customer_number', event.target.value);
                }}
              />
              <span className="field__hint">{t('crm.customerNumberHint')}</span>
            </div>

            <div className="field">
              <label className="field__label" htmlFor="customer-status">
                {t('crm.status')}
              </label>
              <Select
                id="customer-status"
                className="field__control"
                value={values.status}
                onChange={(event) => {
                  set('status', event.target.value as CustomerStatus);
                }}
              >
                {STATUSES.map((value) => (
                  <option key={value} value={value}>
                    {t(`customerStatus.${value}`)}
                  </option>
                ))}
              </Select>
            </div>

            <div className="field">
              <label className="field__label" htmlFor="customer-industry">
                {t('crm.industry')}
              </label>
              <CrmSuggestInput
                id="customer-industry"
                value={values.industry}
                onChange={(next) => {
                  set('industry', next);
                }}
                field="customer.industry"
              />
            </div>

            <div className="field">
              <label className="field__label" htmlFor="customer-vat">
                {t('crm.vatId')}
              </label>
              <input
                id="customer-vat"
                className="field__control"
                value={values.vat_id}
                onChange={(event) => {
                  set('vat_id', event.target.value);
                }}
              />
            </div>

            <div className="field">
              <label className="field__label" htmlFor="customer-website">
                {t('crm.website')}
              </label>
              <input
                id="customer-website"
                className="field__control"
                value={values.website}
                onChange={(event) => {
                  set('website', event.target.value);
                }}
              />
            </div>

            <div className="field">
              <label className="field__label" htmlFor="customer-notes">
                {t('crm.notes')}
              </label>
              <textarea
                id="customer-notes"
                className="field__control field__control--area"
                rows={4}
                value={values.notes}
                onChange={(event) => {
                  set('notes', event.target.value);
                }}
              />
            </div>

            <div className="field">
              <label className="field__label" htmlFor="customer-mail-domains">
                {t('crm.mailDomains')}
              </label>
              <textarea
                id="customer-mail-domains"
                className="field__control field__control--area"
                rows={3}
                value={values.mail_domains}
                onChange={(event) => {
                  set('mail_domains', event.target.value);
                }}
              />
              <span className="field__hint">{t('crm.mailDomainsHint')}</span>
            </div>

            <div className="field">
              <label className="field__checkbox">
                <input
                  type="checkbox"
                  checked={values.is_vip}
                  onChange={(event) => {
                    set('is_vip', event.target.checked);
                  }}
                />
                {t('crm.vipHint')}
              </label>
            </div>

            <div className="field">
              <label className="field__checkbox">
                <input
                  type="checkbox"
                  checked={values.accepts_email_notifications}
                  onChange={(event) => {
                    set('accepts_email_notifications', event.target.checked);
                  }}
                />
                {t('crm.emailNotifications')}
              </label>
              <span className="field__hint">{t('crm.emailNotificationsHint')}</span>
            </div>

            <div className="row" style={{ justifyContent: 'flex-end' }}>
              <Dialog.Close asChild>
                <Button variant="ghost">{t('app.cancel')}</Button>
              </Dialog.Close>
              <Button
                type="submit"
                variant="primary"
                disabled={!values.name.trim()}
                busy={mutation.isPending}
              >
                {t('app.save')}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
